import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ShieldCheck, ArrowLeft, RefreshCw, Mail, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from './AuthLayout';
import OtpInput, { OTP_LENGTH, EMPTY_OTP as EMPTY, focusOtpBox, formatCountdown } from './OtpInput';

const OtpVerification = () => {
  const navigate = useNavigate();
  const { pendingTwoFactor, verifyOtp, resendOtp, cancelTwoFactor } = useAuth();

  const [digits, setDigits] = useState(EMPTY);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  // Set when the current OTP can no longer succeed (expired / too many
  // attempts) - only a resend helps now.
  const [needsNewCode, setNeedsNewCode] = useState(false);
  const [now, setNow] = useState(Date.now());
  const inputsRef = useRef([]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!pendingTwoFactor) navigate('/login', { replace: true });
  }, [pendingTwoFactor, navigate]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  if (!pendingTwoFactor) return null;

  const otp = digits.join('');
  const expiresInMs = pendingTwoFactor.otpExpiresAt - now;
  const resendInMs = pendingTwoFactor.resendAvailableAt - now;
  const expired = expiresInMs <= 0;
  const canResend = resendInMs <= 0 && !resending;
  const canVerify = otp.length === OTP_LENGTH && !verifying && !needsNewCode && !expired;

  const focusBox = (index) => focusOtpBox(inputsRef, index);

  const leaveToLogin = (message) => {
    if (message) toast.error(message);
    cancelTwoFactor();
    navigate('/login', { replace: true });
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError('Please enter the 6-digit verification code.');
      return;
    }
    setVerifying(true);
    setError('');
    const result = await verifyOtp(otp);
    setVerifying(false);

    if (result.success) {
      toast.success('OTP verified successfully.');
      navigate('/dashboard', { replace: true });
      return;
    }
    if (result.code === 'INVALID_CHALLENGE') {
      leaveToLogin(result.message);
      return;
    }
    if (result.code === 'OTP_BLOCKED' || result.code === 'OTP_EXPIRED') {
      setNeedsNewCode(true);
    }
    const remaining = result.attemptsRemaining !== undefined
      ? ` ${result.attemptsRemaining} attempt${result.attemptsRemaining === 1 ? '' : 's'} remaining.`
      : '';
    setError(`${result.message}${remaining}`);
    setDigits(EMPTY);
    focusBox(0);
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResending(true);
    const result = await resendOtp();
    setResending(false);

    if (result.success) {
      toast.success(result.message || 'A new verification code has been sent.');
      setDigits(EMPTY);
      setError('');
      setNeedsNewCode(false);
      focusBox(0);
      return;
    }
    if (result.code === 'INVALID_CHALLENGE' || result.code === 'RESEND_LIMIT') {
      leaveToLogin(result.message);
      return;
    }
    toast.error(result.message);
  };

  return (
    <AuthLayout>
      <style>{`
        .otp-card {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 440px;
          background: white;
          border-radius: 1.25rem;
          box-shadow: 0 25px 60px -15px rgba(22, 66, 176, 0.55);
          padding: 2.25rem 2rem;
          overflow: hidden;
        }
        .otp-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #60a5fa, #1642b0, #60a5fa);
        }
        .otp-verify-btn { transition: filter 0.15s ease; }
        .otp-verify-btn:hover:not(:disabled) { filter: brightness(1.06); }
        .otp-link-btn {
          background: none;
          border: none;
          padding: 0;
          font-size: 0.9rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }
        @media (max-width: 480px) {
          .otp-card { padding: 1.75rem 1.1rem; }
        }
      `}</style>

      <div className="otp-card">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#dbeafe',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem'
          }}>
            <ShieldCheck size={28} color="#1d4ed8" />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem' }}>Two-Step Verification</h2>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
            A 6-digit verification code has been sent to your registered email address
            {pendingTwoFactor.maskedEmail && (
              <>
                {' '}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#1f2937', fontWeight: 600 }}>
                  <Mail size={14} /> {pendingTwoFactor.maskedEmail}
                </span>
              </>
            )}
            . Please enter the OTP to continue.
          </p>
        </div>

        <form onSubmit={handleVerify} noValidate>
          <label style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.75rem' }}>
            Enter 6-digit OTP
          </label>
          <OtpInput
            digits={digits}
            setDigits={setDigits}
            inputsRef={inputsRef}
            disabled={verifying || needsNewCode}
            hasError={Boolean(error)}
            onEdit={() => setError('')}
          />

          <div style={{ minHeight: '1.25rem', textAlign: 'center', marginBottom: '0.75rem' }}>
            {error ? (
              <span role="alert" style={{ fontSize: '0.8rem', color: '#dc2626' }}>{error}</span>
            ) : (
              <span style={{ fontSize: '0.8rem', color: expired ? '#dc2626' : '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                <Clock size={13} />
                {expired ? 'OTP expired. Please request a new OTP.' : `Code expires in ${formatCountdown(expiresInMs)}`}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={!canVerify}
            className="otp-verify-btn"
            style={{
              width: '100%',
              padding: '0.8rem',
              background: canVerify ? 'linear-gradient(90deg, #1642b0, #4f8ef7)' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '0.6rem',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: canVerify ? 'pointer' : 'not-allowed'
            }}
          >
            {verifying ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
          <div style={{ marginBottom: '0.4rem' }}>Didn't receive the code?</div>
          <button
            type="button"
            className="otp-link-btn"
            onClick={handleResend}
            disabled={!canResend}
            style={{ color: canResend ? '#2563eb' : '#9ca3af', cursor: canResend ? 'pointer' : 'not-allowed' }}
          >
            <RefreshCw size={15} />
            {resending ? 'Sending...' : resendInMs > 0 ? `Resend OTP in ${Math.ceil(resendInMs / 1000)}s` : 'Resend OTP'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            className="otp-link-btn"
            onClick={() => leaveToLogin()}
            style={{ color: '#6b7280', cursor: 'pointer', fontWeight: 500 }}
          >
            <ArrowLeft size={15} /> Back to sign in
          </button>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
